import { useSelector } from 'react-redux';
import { PRE_CHECKOUT_STEPS } from 'constants/purchaseSteps';
import useFinalPriceExperiment from 'hooks/useFinalPriceExperiment';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import getPurchaseStepName from 'utils/purchase/getPurchaseStepName';
import { hasDisplayTotal } from 'utils/trips/displayedPrice';
import { hasMarkupAboveTotal } from 'utils/trips/finalPricing';
import { purchaseLegPricingKey, returnLegEarnsRoundTripFare } from 'utils/roundTripFare';

/**
 * The current route, read from the address bar rather than the router context.
 *
 * The seats panel also renders on results (seats-on-results tenants) and in suites with no
 * `<Router>` above it, where `useLocation` throws. `BrowserRouter` keeps the two in step, and every
 * caller re-renders on navigation: `usePricingBeforeCheckout` subscribes to the location, and the
 * seats panel lives on the seats steps only.
 *
 * @returns {string} The pathname, or an empty string outside a browser.
 */
const currentPathname = () =>
  typeof window === 'undefined' ? '' : window.location?.pathname || '';

// The last fragment lists `returnEarnsRoundTripFare` compared, and its answer. Immutable lists
// keep their reference until a leg changes, so every reader on the page hits this on an unrelated
// dispatch and the legs are converted only when one of them changes.
let lastLegs = [];
let lastEarnsRoundTripFare = true;

/**
 * Whether a purchase's return leg earns the round-trip fare when the tenant validates it, with the
 * rule `usePricingBeforeCheckout` applies (`returnLegEarnsRoundTripFare`).
 *
 * @param {Immutable.List} [departFragments] - The depart leg's fragments.
 * @param {Immutable.List} [returnFragments] - The return leg's fragments.
 * @returns {boolean} Whether the return renders `roundTripPricing` rather than `pricing`.
 */
const returnEarnsRoundTripFare = (departFragments, returnFragments) => {
  if (lastLegs[0] === departFragments && lastLegs[1] === returnFragments) {
    return lastEarnsRoundTripFare;
  }

  lastLegs = [departFragments, returnFragments];
  lastEarnsRoundTripFare = returnLegEarnsRoundTripFare({
    validateRoundTripDiscount: true,
    departFragments: departFragments?.toJS?.(),
    returnFragments: returnFragments?.toJS?.(),
  });
  return lastEarnsRoundTripFare;
};

/**
 * Whether this purchase paints the markup-inclusive ("final") price before checkout (BOOK-168).
 *
 * One arm per purchase: the sidebar, its per-seat rows and leg headers, the seats panel and the
 * mobile seat counter all read this value, so a screen never mixes arms. It is gated on each leg's
 * pricing as the sidebar renders it -- the depart leg's `departureRoundTripPricing` on a round trip
 * and `pricing` one way; the return leg's `roundTripPricing` or `pricing`, as `utils/roundTripFare`
 * chooses -- and on the steps that show the estimate with `PRICING_BEFORE_CHECKOUT` on. Either leg
 * counts: a round trip whose markup rule covers only the return paints that return final on
 * results, so the purchase must keep the final arm here too. An INS-1288 `displayTotal` on either
 * leg keeps it in control. Anywhere else (checkout, complete, review, quick checkout, results) the
 * rule receives no markup signal, so GrowthBook is never evaluated there, even with companions in
 * the payload (R2).
 *
 * The rule, the key and the tab latch are `hooks/useFinalPriceExperiment`'s, so this arm agrees
 * with the cards the user came from.
 *
 * @returns {boolean} Whether to paint the final price before checkout.
 */
const usePreCheckoutFinalPrice = () => {
  const features = useWhitelabelFeatures();
  const isRoundTrip = useSelector((state) => Boolean(state.purchase?.get?.('roundTrip')));
  const isExchange = useSelector((state) => Boolean(state.purchase?.get?.('isExchange')));
  const validateRoundTripDiscount = Boolean(features?.VALIDATE_ROUND_TRIP_DISCOUNT);
  // The tenant flag short-circuits before the legs are converted, as in the shared rule.
  const earnsRoundTripFare = useSelector(
    (state) =>
      !isRoundTrip ||
      !validateRoundTripDiscount ||
      returnEarnsRoundTripFare(
        state.purchase?.getIn?.(['departs', 'fragments']),
        state.purchase?.getIn?.(['returns', 'fragments']),
      ),
  );
  // The keys the sidebar renders (`usePricingBeforeCheckout`), from the same helper.
  const departPricingKey = purchaseLegPricingKey({ way: 'departs', isRoundTrip });
  const returnPricingKey = purchaseLegPricingKey({ way: 'returns', earnsRoundTripFare });
  /**
   * Selects one primitive of a leg's pricing, so the selectors never hand back objects.
   *
   * @param {string} leg - `departs` or `returns`.
   * @param {string} pricingKey - The pricing object the sidebar renders for that leg.
   * @param {string} field - The pricing field.
   * @returns {Function} The selector.
   */
  const selectLegPricing = (leg, pricingKey, field) => (state) =>
    state.purchase?.getIn?.([leg, 'fragments', 0, pricingKey, field]);
  const total = useSelector(selectLegPricing('departs', departPricingKey, 'total'));
  const totalWithMarkup = useSelector(
    selectLegPricing('departs', departPricingKey, 'totalWithMarkup'),
  );
  const displayTotal = useSelector(selectLegPricing('departs', departPricingKey, 'displayTotal'));
  const returnTotal = useSelector(selectLegPricing('returns', returnPricingKey, 'total'));
  const returnTotalWithMarkup = useSelector(
    selectLegPricing('returns', returnPricingKey, 'totalWithMarkup'),
  );
  const returnDisplayTotal = useSelector(
    selectLegPricing('returns', returnPricingKey, 'displayTotal'),
  );
  // A one-way purchase has no return leg to read.
  const returnHasMarkup =
    isRoundTrip &&
    hasMarkupAboveTotal({ total: returnTotal, totalWithMarkup: returnTotalWithMarkup });
  const returnHasDisplayTotal =
    isRoundTrip && hasDisplayTotal({ displayTotal: returnDisplayTotal });
  const isBeforeCheckout = PRE_CHECKOUT_STEPS.includes(getPurchaseStepName(currentPathname()));
  // Same predicate as `usePricingBeforeCheckout`'s `displayPricingBeforeCheckout`.
  const displaysPricingBeforeCheckout =
    isBeforeCheckout && Boolean(features?.PRICING_BEFORE_CHECKOUT);

  return useFinalPriceExperiment({
    hasMarkup:
      displaysPricingBeforeCheckout &&
      (hasMarkupAboveTotal({ total, totalWithMarkup }) || returnHasMarkup),
    hasDisplayTotal: hasDisplayTotal({ displayTotal }) || returnHasDisplayTotal,
    isExchange,
  });
};

export default usePreCheckoutFinalPrice;
